import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmt, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfidenceBadge } from "@/components/ConfidenceBadge";
import { AIAssistant } from "@/components/AIAssistant";
import { USSalaryModelTab } from "@/components/USSalaryModelTab";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend, LabelList,
} from "recharts";
import {
  FileText, FileSpreadsheet, FileType, Loader2, TrendingUp, TrendingDown,
  ArrowRight, CheckCircle2, Circle, ShieldAlert, Info,
} from "lucide-react";

const STAGES = ["Draft", "Data Review", "Tax Review", "Mobility Review", "Approved", "Employee Issued", "Superseded"];
const STAGE_COLORS = { Draft: "#64748B", "Data Review": "#0284C7", "Tax Review": "#7C3AED", "Mobility Review": "#D97706", Approved: "#059669", "Employee Issued": "#2563EB", Superseded: "#475569" };

function Metric({ label, value, sub, accent, bar = "#F5A623", testId }) {
  return (
    <div className="relative overflow-hidden rounded-sm border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors duration-200" data-testid={testId}>
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: bar }} />
      <div className="term-label flex items-center justify-between">{label}</div>
      <div className={`font-mono-fin font-extrabold text-2xl sm:text-3xl mt-1.5 tabular tracking-tight ${accent || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-[#94A3B8] mt-1">{sub}</div>}
    </div>
  );
}

const TT = { backgroundColor: "#FFFFFF", borderColor: "#E2E8F0", borderRadius: "8px", color: "#0F172A", fontSize: "12px", fontFamily: "JetBrains Mono", boxShadow: "0 8px 24px rgba(15,23,42,0.10)" };

function SRow({ label, note, v, sign, bold, total, accent }) {
  const color = total || accent === "amber" ? "text-[#B45309]" : sign === "−" ? "text-[#DC2626]" : (bold ? "text-slate-900" : "text-slate-700");
  return (
    <div className={`flex items-center justify-between gap-4 px-5 ${total ? "py-3 bg-[#D97706]/[0.08] border-y-2 border-[#D97706]/30" : bold ? "py-2.5 border-t border-slate-200" : "py-2 border-t border-slate-100"}`}>
      <div className="min-w-0">
        <div className={`text-sm ${bold || total ? "font-bold" : ""} ${total ? "text-[#B45309] tracking-wide uppercase" : "text-slate-700"}`}>
          {sign && <span className={`mr-1.5 font-mono ${sign === "+" ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{sign}</span>}{label}
        </div>
        {note && <div className="text-[11px] text-slate-400 mt-0.5">{note}</div>}
      </div>
      <div className={`font-mono-fin tabular text-right shrink-0 ${bold || total ? "text-base font-extrabold" : "text-sm"} ${color}`}>{fmt(v, "USD")}</div>
    </div>
  );
}

export default function IndividualDashboard() {
  const { id } = useParams();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [exporting, setExporting] = useState("");
  const [scenario, setScenario] = useState({ fx: 0, rent: 0, cola: 0 });
  const [scResult, setScResult] = useState(null);
  const [guide, setGuide] = useState(null);
  const [advancing, setAdvancing] = useState(false);
  const [switching, setSwitching] = useState("");
  const [period, setPeriod] = useState("annual");
  const [locations, setLocations] = useState([]);
  const [levels, setLevels] = useState([]);
  const [whatIf, setWhatIf] = useState(null);
  const [wfRes, setWfRes] = useState(null);
  const load = () => api.get(`/calculations/${id}`).then((r) => setDoc(r.data));
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (doc) api.get(`/guide/${doc.host_key}?base_key=${doc.base_key}`).then((r) => setGuide(r.data)).catch(() => {});
  }, [doc]);
  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/meta").then((r) => setLevels(r.data.level_options || [])).catch(() => {});
  }, []);

  const runScenario = async (s) => {
    setScenario(s);
    const { data } = await api.post("/scenario", {
      calculation_id: id, fx_delta_pct: s.fx, rent_delta_pct: s.rent, cola_delta_pct: s.cola,
    });
    setScResult(data);
  };

  const doExport = async (fmtType) => {
    setExporting(fmtType);
    try {
      const resp = await fetch(`${API}/export/${id}/${fmtType}`, { credentials: "include" });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `ESSAR_assignment_${id.slice(0, 8)}.${fmtType}`;
      a.click(); URL.revokeObjectURL(url);
    } finally { setExporting(""); }
  };

  const doOfferLetter = async () => {
    setExporting("offer");
    try {
      const resp = await fetch(`${API}/export/${id}/offer`, { credentials: "include" });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Mesabi_OfferLetter_${(doc.employee_name || "employee").replace(/ /g, "_")}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } finally { setExporting(""); }
  };

  const runWhatIf = async (host, tier) => {
    setWhatIf({ host, tier });
    try {
      const { data } = await api.post("/preview", { employee_id: doc.employee_id, base_key: doc.base_key, host_key: host, grade_band: tier });
      if (data.status === "complete") setWfRes(data.result);
    } catch { /* ignore */ }
  };

  const advance = async (action) => {
    setAdvancing(true);
    try { await api.post("/workflow/advance", { calculation_id: id, action, note: "" }); await load(); }
    catch (e) { alert(e.response?.data?.detail || "Cannot advance"); }
    finally { setAdvancing(false); }
  };

  const switchPolicy = async (p) => {
    setSwitching(p);
    try { await api.post(`/calculations/${id}/repolicy`, { tax_policy: p }); await load(); }
    catch (e) { alert(e.response?.data?.detail || "Cannot switch policy"); }
    finally { setSwitching(""); }
  };

  if (!doc) return <div className="p-8 flex items-center gap-2 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading calculation…</div>;

  const r = doc.result;
  const s = r.summary;
  const hc = r.host_currency, homc = r.home_currency;
  const canApprove = ["hr_business_partner", "mobility_specialist", "comp_admin", "tax_reviewer", "system_administrator"].includes(user.role);
  const per = (v) => (period === "monthly" ? v / 12 : v);

  // Waterfall (home currency purchasing-power build to Target Net)
  const wf = [
    { name: "Home Spendable", v: s.home_spendable.home },
    { name: "COLA", v: s.cola.home },
    { name: "Housing", v: s.housing_support.home },
    { name: "Education", v: s.education.home },
    { name: "Hardship", v: s.hardship.home },
    { name: "Special", v: s.special_allowance.home },
  ];
  let cum = 0;
  const wfData = wf.map((x) => { const base = x.v >= 0 ? cum : cum + x.v; cum += x.v; return { name: x.name, base: Math.max(base, 0), value: Math.abs(x.v), raw: x.v, neg: x.v < 0 }; });
  wfData.push({ name: "Target Net", base: 0, value: cum, raw: cum, neg: false, total: true });

  // Home vs Host spendable bridge (USD common)
  const bridge = [
    { name: "Home", spendable: s.home_spendable.usd },
    { name: "Host (req.)", spendable: s.home_spendable.usd + s.cola.usd },
  ];

  const compData = [
    { name: "Tax", Home: 0, Host: s.host_tax.usd },
    { name: "Housing", Home: 0, Host: s.housing_support.usd },
    { name: "Education", Home: 0, Host: s.education.usd },
    { name: "Saving", Home: s.home_saving_capacity.usd, Host: s.host_saving_capacity.usd },
  ];

  // Current (home baseline) vs Offered (host assignment) — every package component, USD
  const cvo = [
    { name: "Spendable", Current: per(s.home_spendable.usd), Offered: per(s.home_spendable.usd + s.cola.usd) },
    { name: "Housing", Current: per(s.home_housing_norm?.usd || 0), Offered: per(s.host_housing_total?.usd || 0) },
    { name: "Education", Current: 0, Offered: per(s.education.usd) },
    { name: "Hardship", Current: 0, Offered: per(s.hardship.usd) },
    { name: "Special", Current: 0, Offered: per(s.special_allowance.usd) },
    { name: "Savings", Current: per(s.savings?.usd || 0), Offered: per(s.host_saving_capacity.usd) },
    { name: "Income Tax", Current: per(s.hypothetical_tax.usd), Offered: per(s.host_tax.usd) },
  ];

  // Balance-sheet compensation statement (USD, single currency, reconciled to summary)
  const stmt = [
    { section: "Home Country Baseline", rows: [
      { label: "Home Reference Compensation", note: "Base salary + recurring fixed cash (line A)", v: s.home_reference_comp.usd },
      { label: "Hypothetical Home Tax", note: "Notional home tax the employee always bears (line B)", v: s.hypothetical_tax.usd, sign: "−" },
      { label: "Home Social Security", note: "Employee statutory contributions (line B2)", v: s.home_social?.usd || 0, sign: "−" },
      { label: "Home Housing Norm", note: "Housing already funded from home pay (line C)", v: s.home_housing_norm.usd, sign: "−" },
      { label: "Policy Savings (retained)", note: "Non-spendable savings component (line C2)", v: s.savings.usd, sign: "−" },
    ], subtotal: { label: "Home Spendable Income", note: "Money available for day-to-day living (line D)", v: s.home_spendable.usd } },
    { section: "Assignment Balance-Sheet Adjustments", rows: [
      { label: "Home Spendable Income (carried)", v: s.home_spendable.usd },
      { label: "Cost-of-Living Adjustment (COLA)", note: `${(s.cola.pct * 100).toFixed(1)}% ${s.cola.positive ? "augmentation" : "reduction"} (line E)`, v: Math.abs(s.cola.usd), sign: s.cola.usd >= 0 ? "+" : "−" },
      { label: "Policy Savings (preserved)", note: "Savings protected on assignment (line C2)", v: s.savings.usd, sign: "+" },
      { label: "Host Housing Support", note: "Employer-paid rent benefit-in-kind (line F)", v: Math.abs(s.housing_support.usd), sign: s.housing_support.usd >= 0 ? "+" : "−" },
      { label: "Education Support", note: "International-school tuition (line G)", v: s.education.usd, sign: "+" },
      { label: "Hardship Allowance", note: "Quality-of-living premium (line H)", v: s.hardship.usd, sign: "+" },
      { label: "Special Allowance", note: "User-provided allowance (line I)", v: s.special_allowance.usd, sign: "+" },
      { label: "Mobility Premium", note: "Assignment incentive (line I2)", v: s.mobility_premium?.usd || 0, sign: "+" },
      { label: "Assignment Bonus (% of base)", note: "User-set base-linked bonus (line I3)", v: s.assignment_bonus?.usd || 0, sign: "+" },
      { label: "Home-Leave Travel", note: "Tier flight entitlement (line T)", v: s.travel?.usd || 0, sign: "+" },
    ], subtotal: { label: "Target Net Assignment Package", note: "Net purchasing power to preserve (line K)", v: s.target_net_package.usd, accent: "amber" } },
    { section: "Host Taxation & Gross-Up", rows: [
      { label: "Target Net Package (carried)", v: s.target_net_package.usd },
      { label: "Host Income Tax — grossed up", note: `Deterministic ${r.host_location.country_code} tax · ${r.tax_policy} (line L)`, v: s.host_tax.usd, sign: "+" },
      { label: "Host Employee Social Security", note: "Host statutory contributions (line L2)", v: s.host_social.usd, sign: "+" },
    ], subtotal: { label: "Gross Assignment Cash", note: "Policy-adjusted gross compensation (line M)", v: s.gross_assignment.usd } },
    { section: "Employer Cost Build-Up", rows: [
      { label: "Gross Assignment Cash (carried)", v: s.gross_assignment.usd },
      { label: "Target Bonus", note: "Assignment base × bonus % (line N)", v: s.target_bonus.usd, sign: "+" },
      { label: "Employer Statutory & Benefits", note: "Social + medical + pension + insurance (line O)", v: s.employer_statutory.usd, sign: "+" },
    ], total: { label: "Total Employer Cost", note: "Full cost to company (line P)", v: s.total_employer_cost.usd } },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto animate-fade-up">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Individual Assignment</div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900" data-testid="indiv-employee-name">{doc.employee_name}</h1>
          <div className="flex items-center gap-2 mt-1.5 text-muted-foreground">
            <span className="font-medium">{r.base_location.city}, {r.base_location.country}</span>
            <ArrowRight className="h-4 w-4 text-essred" />
            <span className="font-medium">{r.host_location.city}, {r.host_location.country}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ConfidenceBadge level={r.confidence} testId="indiv-confidence-badge" />
          <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: STAGE_COLORS[doc.status] }} data-testid="indiv-status-badge">{doc.status}</span>
        </div>
      </div>

      {/* Data source + policy banner */}
      <div className="mt-4 rounded-xl bg-slate-50 text-slate-400 px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="indiv-source-banner">
        <span className="text-gold font-semibold">{r.data_source_label}</span>
        <span>Reference: {r.reference_period}</span>
        <span>Retrieved: {r.retrieved_at?.slice(0, 10)}</span>
        <span>Engine v{r.engine_version} · Tax v{r.tax_engine_version}</span>
        <span>Policy: {r.tax_policy}</span>
        {r.benefit_plan && <span className="text-gold font-semibold" data-testid="indiv-plan-badge">Plan: {r.benefit_plan.label}</span>}
        {r.confidence === "low" && <span className="flex items-center gap-1 text-amber-300"><ShieldAlert className="h-4 w-4" /> Low-confidence data — review required</span>}
      </div>

      {/* Policy switch (reviewers) */}
      {canApprove && (
        <div className="mt-4 rounded-xl border bg-white p-4 flex flex-wrap items-center gap-3" data-testid="policy-switch">
          <span className="term-label">Tax Policy</span>
          {[["equalisation", "Equalisation"], ["protection", "Protection"], ["no_equalisation", "No Equalisation"]].map(([k, lbl]) => (
            <button key={k} data-testid={`policy-switch-${k}`} disabled={!!switching}
              onClick={() => switchPolicy(k)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${r.tax_policy === k ? "bg-[#F5A623] text-[#0A0E14]" : "bg-slate-100 text-slate-300 hover:bg-[#232B36]"}`}>
              {switching === k ? "…" : lbl}
            </button>
          ))}
          <span className="text-xs text-muted-foreground ml-auto">Switches instantly &amp; re-runs the gross-up</span>
        </div>
      )}

      {/* KPIs */}
      {/* period toggle + spendable */}
      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="period-toggle">
        <span className="term-label">View</span>
        {[["annual", "Annual"], ["monthly", "Monthly"]].map(([k, l]) => (
          <button key={k} data-testid={`period-${k}`} onClick={() => setPeriod(k)}
            className={`px-3 py-1 rounded-md text-sm font-semibold ${period === k ? "bg-[#F5A623] text-[#0A0E14]" : "bg-slate-100 text-slate-300 hover:bg-[#232B36]"}`}>{l}</button>
        ))}
        <span className="text-xs text-muted-foreground">Amounts shown {period === "monthly" ? "per month" : "per year"}</span>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="spendable-panel">
        <div className="rounded-xl border bg-white p-5 exec-shadow">
          <div className="term-label">Home Spendable Income</div>
          <div className="font-mono font-bold text-2xl text-slate-900 mt-1">{fmt(per(s.home_spendable.home), homc)}</div>
          <div className="text-xs text-muted-foreground mt-1">{fmt(per(s.home_spendable.usd), "USD")} (USD) · {period}</div>
        </div>
        <div className="rounded-xl border bg-white p-5 exec-shadow">
          <div className="term-label">Host Spendable Requirement</div>
          <div className="font-mono font-bold text-2xl text-[#F5A623] mt-1">{fmt(per(s.home_spendable.home + s.cola.home), homc)}</div>
          <div className="text-xs text-muted-foreground mt-1">Home spendable × location cost ratio · {period}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Metric testId="kpi-total-employer-cost" label="Total Employer Cost" bar="#F5A623" value={fmt(per(s.total_employer_cost.host), hc)} sub={fmt(per(s.total_employer_cost.usd), "USD") + " (USD)"} />
        <Metric testId="kpi-employee-net" label="Employee Net Cash" bar="#0284C7" value={fmt(per(s.employee_net_cash.host), hc)} sub="After host tax & social" />
        <Metric testId="kpi-cola" label="COLA" bar={s.cola.positive ? "#26D07C" : "#DC2626"} value={`${(s.cola.pct * 100).toFixed(1)}%`} accent={s.cola.positive ? "text-[#26D07C]" : "text-[#DC2626]"} sub={s.cola.positive ? "Positive (augmentation)" : "Negative (cost reduction)"} />
        <Metric testId="kpi-saving" label="Host Saving Capacity" bar="#26D07C" value={fmt(per(s.host_saving_capacity.host), hc)} sub={`Home: ${fmt(per(s.home_saving_capacity.home), homc)}`} />
      </div>

      {/* Current vs Offered — headline graphical comparison */}
      <Card className="mt-6 p-5 bg-white border border-slate-200 rounded-sm" data-testid="current-vs-offered">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
          <h3 className="font-display font-bold text-slate-900">Current vs Offered — Full Package (USD)</h3>
          <span className="term-label">Home baseline · Host assignment</span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Every balance-sheet component: what the employee has at home today versus what the assignment offers. USD, {period}.</p>
        <ResponsiveContainer width="100%" height={340}>
          <BarChart data={cvo} margin={{ top: 20, right: 10, left: 0, bottom: 10 }} barGap={4}>
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={{ stroke: "#253040" }} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} />
            <Tooltip contentStyle={TT} cursor={{ fill: "rgba(255,255,255,0.03)" }} formatter={(v, n) => [fmt(v, "USD"), n]} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
            <Bar dataKey="Current" name="Current (Home)" fill="#94A3B8" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Offered" name="Offered (Host)" fill="#C8102E" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
          <div className="rounded-sm border border-slate-200 bg-slate-50 p-3"><div className="term-label">Current Home Package</div><div className="font-mono-fin font-bold text-lg text-slate-700 mt-1">{fmt(per(s.home_reference_comp.usd), "USD")}</div></div>
          <div className="rounded-sm border border-slate-200 bg-slate-50 p-3"><div className="term-label">Offered Net Package</div><div className="font-mono-fin font-bold text-lg text-[#F5A623] mt-1">{fmt(per(s.target_net_package?.usd || 0), "USD")}</div></div>
          <div className="rounded-sm border border-slate-200 bg-slate-50 p-3"><div className="term-label">Total Employer Cost</div><div className="font-mono-fin font-bold text-lg text-[#26D07C] mt-1">{fmt(per(s.total_employer_cost.usd), "USD")}</div></div>
        </div>
      </Card>

      {r.reverse && (
        <Card className="mt-6 p-5 bg-white term-glow-amber" data-testid="reverse-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display font-bold text-gold">Reverse-Solved to Target CTC</h3>
            <span className="text-xs text-muted-foreground">Factor ×{r.reverse.factor} · user-provided, requires review</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-3 text-sm">
            <div><div className="term-label">Target CTC</div><div className="font-mono font-bold text-slate-900">{fmt(r.reverse.target_host, hc)}</div></div>
            <div><div className="term-label">Computed TEC</div><div className="font-mono text-slate-300">{fmt(r.reverse.computed_tec_host, hc)}</div></div>
            <div><div className="term-label">Scaled Employee Net</div><div className="font-mono text-slate-900">{fmt(r.reverse.scaled.employee_net_cash, hc)}</div></div>
            <div><div className="term-label">Scaled Gross</div><div className="font-mono text-slate-900">{fmt(r.reverse.scaled.gross_assignment, hc)}</div></div>
          </div>
          {r.reverse.breakdown && (
            <div className="mt-3 text-sm grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reverse-breakdown">
              <div><div className="term-label">Fixed (housing+edu+…)</div><div className="font-mono text-slate-300">{fmt(r.reverse.breakdown.fixed_host, hc)}</div></div>
              <div><div className="term-label">Base (flexed)</div><div className="font-mono text-slate-900">{fmt(r.reverse.breakdown.base_new_host, hc)}</div></div>
              <div><div className="term-label">Base scale</div><div className="font-mono text-gold">×{r.reverse.breakdown.base_scale}</div></div>
              <div><div className="term-label">New Net</div><div className="font-mono text-slate-900">{fmt(r.reverse.breakdown.new_net_host, hc)}</div></div>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">{r.reverse.note}</p>
        </Card>
      )}

      <Tabs defaultValue="statement" className="mt-6">
        <TabsList className="bg-white border flex-wrap h-auto">
          <TabsTrigger value="statement" data-testid="tab-statement">Statement</TabsTrigger>
          <TabsTrigger value="waterfall" data-testid="tab-waterfall">Waterfall</TabsTrigger>
          <TabsTrigger value="lines" data-testid="tab-lines">Calculation Lines</TabsTrigger>
          <TabsTrigger value="comparison" data-testid="tab-comparison">Comparison</TabsTrigger>
          <TabsTrigger value="detail" data-testid="tab-detail">COLA Comparator &amp; Tax</TabsTrigger>
          <TabsTrigger value="usmodel" data-testid="tab-usmodel">US Salary Model</TabsTrigger>
          <TabsTrigger value="scenario" data-testid="tab-scenario">Scenario</TabsTrigger>
          <TabsTrigger value="sources" data-testid="tab-sources">Assumptions & Sources</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="tab-workflow">Workflow</TabsTrigger>
          <TabsTrigger value="guide" data-testid="tab-guide">City Guide</TabsTrigger>
        </TabsList>

        {/* Compensation Balance-Sheet Statement */}
        <TabsContent value="statement">
          <Card className="mb-4 panel p-4" data-testid="whatif-panel">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <span className="term-label text-[#F5A623]">What-If · Live Re-compute</span>
                <p className="text-xs text-muted-foreground mt-0.5">Flip the host city or tier and watch the package recompute instantly (preview only — not saved).</p>
              </div>
              <div className="flex gap-2">
                <select value={whatIf?.host || doc.host_key} data-testid="whatif-host"
                  onChange={(e) => runWhatIf(e.target.value, whatIf?.tier || r.benefit_plan?.key)}
                  className="bg-slate-50 border border-slate-200 rounded-sm text-sm px-2.5 py-2 text-slate-700 focus:border-[#F5A623] outline-none">
                  {locations.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                </select>
                <select value={whatIf?.tier || r.benefit_plan?.key} data-testid="whatif-tier"
                  onChange={(e) => runWhatIf(whatIf?.host || doc.host_key, e.target.value)}
                  className="bg-slate-50 border border-slate-200 rounded-sm text-sm px-2.5 py-2 text-slate-700 focus:border-[#F5A623] outline-none">
                  {levels.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
            </div>
            {wfRes && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4" data-testid="whatif-results">
                {[["Total Employer Cost", "total_employer_cost"], ["Employee Net", "employee_net_cash"], ["Saving Capacity", "host_saving_capacity"], ["COLA", "cola"]].map(([lbl, key]) => {
                  const cur = key === "cola" ? s.cola.pct * 100 : s[key].usd;
                  const nw = key === "cola" ? wfRes.summary.cola.pct * 100 : wfRes.summary[key].usd;
                  const same = Math.abs(nw - cur) < 0.5;
                  const up = nw > cur;
                  return (
                    <div key={key} className="panel p-3">
                      <div className="term-label">{lbl}</div>
                      <div className="font-mono-fin font-bold text-slate-900 mt-1">{key === "cola" ? nw.toFixed(1) + "%" : fmt(nw, "USD")}</div>
                      <div className={`text-[11px] font-mono-fin mt-0.5 ${same ? "text-slate-400" : up ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{same ? "—" : up ? "▲" : "▼"} was {key === "cola" ? cur.toFixed(1) + "%" : fmt(cur, "USD")}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
          <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="comp-statement">
            <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 bg-slate-50">
              <div>
                <div className="font-display font-bold text-slate-900 text-lg">Compensation Balance-Sheet Statement</div>
                <div className="text-xs text-[#64748B] mt-0.5">{doc.employee_name} · {r.base_location.city} → {r.host_location.city} · {r.tax_policy} · {r.data_source_label}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right"><div className="term-label">Reporting</div><div className="font-mono-fin text-sm text-[#94A3B8]">USD · {period}</div></div>
                <Button data-testid="offer-letter-btn" onClick={doOfferLetter} disabled={exporting === "offer"} className="bg-[#F5A623] hover:bg-[#E69512] text-[#0A0E14] font-bold rounded-sm h-9">
                  {exporting === "offer" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}Offer Letter PDF
                </Button>
              </div>
            </div>
            {stmt.map((sec) => (
              <div key={sec.section} data-testid={`stmt-section-${sec.section.toLowerCase().split(" ")[0]}`}>
                <div className="px-5 py-2 bg-slate-50/60 border-y border-[#1B232E]">
                  <span className="term-label text-[#F5A623]">{sec.section}</span>
                </div>
                {sec.rows.map((row) => <SRow key={row.label} {...row} v={per(row.v)} />)}
                {sec.subtotal && <SRow {...sec.subtotal} v={per(sec.subtotal.v)} bold />}
                {sec.total && <SRow {...sec.total} v={per(sec.total.v)} total />}
              </div>
            ))}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-5 bg-slate-50 border-t border-slate-200">
              <div><div className="term-label">Employee Net Cash</div><div className="font-mono-fin font-bold text-lg text-[#0284C7] mt-1">{fmt(per(s.employee_net_cash.usd), "USD")}</div></div>
              <div><div className="term-label">Host Saving Capacity</div><div className="font-mono-fin font-bold text-lg text-[#26D07C] mt-1">{fmt(per(s.host_saving_capacity.usd), "USD")}</div></div>
              <div><div className="term-label">Effective COLA</div><div className={`font-mono-fin font-bold text-lg mt-1 ${s.cola.positive ? "text-[#26D07C]" : "text-[#DC2626]"}`}>{(s.cola.pct * 100).toFixed(1)}%</div></div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 text-[11px] text-[#64748B] flex items-start gap-2">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Single-currency (USD) balance-sheet view. Green (+) additions preserve or augment purchasing power; red (−) are home-baseline deductions. Policy Savings appears twice by design — ring-fenced out of Home Spendable (line C2), then added back into the preserved assignment package. The full per-line ledger (native currencies, lines A–R) is in the Calculation Lines tab. Figures: {r.data_source_label} — not Mercer.
            </div>
          </Card>
        </TabsContent>

        {/* Waterfall */}
        <TabsContent value="waterfall">
          <Card className="p-5 bg-white" data-testid="indiv-spendable-bridge">
            <h3 className="font-display font-bold text-slate-900 mb-1">Sequential Compensation Waterfall</h3>
            <p className="text-sm text-muted-foreground mb-4">Balance-sheet build-up to Target Net Package ({homc}). Red bars are balance-sheet reductions (e.g. negative COLA).</p>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={wfData} margin={{ top: 20, right: 10, left: 0, bottom: 30 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} />
                <Tooltip formatter={(v, n, p) => [fmt(p.payload.raw, homc), "Amount"]} />
                <Bar dataKey="base" stackId="a" fill="transparent" />
                <Bar dataKey="value" stackId="a" radius={[4, 4, 0, 0]}>
                  {wfData.map((d, i) => (
                    <Cell key={i} fill={d.total ? "#F5A623" : d.neg ? "#C8102E" : "#059669"} />
                  ))}
                  <LabelList dataKey="raw" position="top" formatter={(v) => fmt(v, homc)} style={{ fontSize: 10, fontWeight: 600 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
          <Card className="p-5 bg-white mt-4">
            <h3 className="font-display font-bold text-slate-900 mb-4">Home vs Host Spendable Bridge (USD)</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={bridge}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} />
                <Tooltip formatter={(v) => fmt(v, "USD")} />
                <Bar dataKey="spendable" fill="#4C9AFF" radius={[6, 6, 0, 0]}>
                  <LabelList dataKey="spendable" position="top" formatter={(v) => fmt(v, "USD")} style={{ fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        {/* Calculation lines */}
        <TabsContent value="lines">
          <Card className="bg-white overflow-hidden" data-testid="indiv-calc-lines">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-400">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Ref</th>
                  <th className="text-left px-4 py-3 font-semibold">Component</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Formula</th>
                  <th className="text-right px-4 py-3 font-semibold">Value</th>
                </tr>
              </thead>
              <tbody>
                {r.lines.map((ln, i) => (
                  <tr key={ln.step} data-testid={`calc-line-${ln.step}`} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-4 py-3 font-mono text-xs text-essred font-bold align-top">{ln.step}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 align-top">
                      {ln.label}
                      {ln.meta?.manual_override && <span className="ml-2 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">OVERRIDE</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground hidden md:table-cell align-top max-w-md">{ln.formula}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold align-top ${ln.value < 0 ? "text-essred" : "text-slate-900"}`}>{fmt(per(ln.value), ln.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        {/* Comparison */}
        <TabsContent value="comparison">
          <Card className="p-5 bg-white">
            <h3 className="font-display font-bold text-slate-900 mb-4">Home vs Host — Tax, Housing, Education & Saving (USD)</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={compData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v / 1000).toFixed(0) + "k"} />
                <Tooltip formatter={(v) => fmt(v, "USD")} />
                <Legend />
                <Bar dataKey="Home" fill="#3A4453" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Host" fill="#F5A623" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </TabsContent>

        {/* Tax & COLA Detail */}
        <TabsContent value="detail">
          <Card className="p-5 bg-white" data-testid="cola-comparator">
            {(() => {
              const cats = r.cola_categories || [];
              const hostName = r.host_location.city;
              const homeName = r.base_location.city;
              const overallHomeRel = Math.round(100 / (1 + s.cola.pct));
              const data = [
                { label: "Overall Basket", host: 100, home: overallHomeRel },
                ...cats.map((c) => ({ label: c.label, host: 100, home: c.host_index ? Math.round((c.home_index / c.host_index) * 100) : 0 })),
              ];
              return (
                <>
                  <h3 className="font-display font-bold text-slate-900 mb-1">COLA Side-by-Side Comparator</h3>
                  <p className="text-sm text-muted-foreground mb-3">Host city <b className="text-[#C8102E]">{hostName}</b> is indexed to <b>100</b>; each bar shows what <b className="text-slate-700">{homeName}</b> (home) costs for the same goods &amp; services basket. Below 100 = home is cheaper (a positive COLA is paid on assignment); above 100 = home is pricier.</p>
                  <div className="flex flex-wrap gap-3 mb-4">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5"><div className="term-label">{hostName} · Host</div><div className="font-mono-fin font-bold text-xl text-[#C8102E]" data-testid="cola-host-index">100</div></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5"><div className="term-label">{homeName} · Home</div><div className="font-mono-fin font-bold text-xl text-slate-700" data-testid="cola-home-index">{overallHomeRel}</div></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5"><div className="term-label">Effective COLA</div><div className={`font-mono-fin font-bold text-xl ${s.cola.positive ? "text-[#16A34A]" : "text-[#DC2626]"}`}>{(s.cola.pct * 100).toFixed(1)}%</div></div>
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(240, data.length * 46)}>
                    <BarChart data={data} layout="vertical" margin={{ left: 10, right: 34, top: 4, bottom: 4 }} barCategoryGap="24%">
                      <XAxis type="number" domain={[0, (dataMax) => Math.ceil((dataMax + 15) / 10) * 10]} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                      <YAxis type="category" dataKey="label" width={128} tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={TT} cursor={{ fill: "rgba(2,6,23,0.03)" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="host" name={`${hostName} (host = 100)`} fill="#C8102E" radius={[0, 4, 4, 0]}>
                        <LabelList dataKey="host" position="right" style={{ fontSize: 11, fill: "#334155", fontFamily: "JetBrains Mono" }} />
                      </Bar>
                      <Bar dataKey="home" name={`${homeName} (home)`} fill="#94A3B8" radius={[0, 4, 4, 0]}>
                        <LabelList dataKey="home" position="right" style={{ fontSize: 11, fill: "#334155", fontFamily: "JetBrains Mono" }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </>
              );
            })()}
          </Card>

          <Card className="p-5 bg-white mt-4" data-testid="indiv-cola-detail">
            <h3 className="font-display font-bold text-slate-900 mb-1">Category-Weighted COLA Basket</h3>
            <p className="text-sm text-muted-foreground mb-4">Location Cost Ratio is the expenditure-weighted ratio of host vs home per-category price indices (New York = 100). Housing, education, tax and social security are excluded here.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wide font-semibold">Category</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wide font-semibold">Weight</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wide font-semibold">Home Idx</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wide font-semibold">Host Idx</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wide font-semibold">Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {(r.cola_categories || []).map((c) => (
                    <tr key={c.category} className="border-t" data-testid={`cola-cat-${c.category}`}>
                      <td className="px-3 py-2 font-medium text-slate-900">{c.label}</td>
                      <td className="px-3 py-2 text-right font-mono">{(c.weight * 100).toFixed(0)}%</td>
                      <td className="px-3 py-2 text-right font-mono">{c.home_index}</td>
                      <td className="px-3 py-2 text-right font-mono">{c.host_index}</td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${c.ratio >= 1 ? "text-emerald-600" : "text-essred"}`}>{c.ratio.toFixed(2)}×</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-5 bg-white mt-4" data-testid="indiv-tax-layers">
            <h3 className="font-display font-bold text-slate-900 mb-4">Host Income Tax — Deterministic Layers</h3>
            {(() => {
              const L = r.lines.find((l) => l.step === "L");
              const det = L?.meta?.detail || {};
              return (
                <div className="grid sm:grid-cols-3 gap-4">
                  <Metric label="National Income Tax" value={fmt(det.national_tax, hc)} />
                  <Metric label={det.state_local?.name || "State / Local"} value={fmt(det.state_local_tax, hc)} sub={det.state_local_tax ? "Sub-national layer" : "None applicable"} />
                  <Metric label="Total Host Income Tax" value={fmt(det.tax, hc)} sub={`Effective ${(det.effective_rate * 100).toFixed(1)}%`} accent="text-essred" />
                </div>
              );
            })()}
          </Card>

          <Card className="p-5 bg-white mt-4" data-testid="indiv-tax-policy-comparison">
            <h3 className="font-display font-bold text-slate-900 mb-1">Tax-Policy Comparison (USD)</h3>
            <p className="text-sm text-muted-foreground mb-4">Company vs employee tax burden under each method. Selected policy: <span className="font-semibold text-slate-900">{r.tax_comparison?.selected_policy}</span>. Hypothetical home tax: {fmt(r.tax_comparison?.hypothetical_home_tax_usd, "USD")} · Actual host tax: {fmt(r.tax_comparison?.actual_host_tax_usd, "USD")}.</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[["equalisation", "Tax Equalisation"], ["protection", "Tax Protection"], ["no_equalisation", "No Equalisation"]].map(([k, lbl]) => {
                const tc = r.tax_comparison?.[k] || {};
                const selected = r.tax_comparison?.selected_policy === k;
                return (
                  <div key={k} data-testid={`tax-policy-${k}`} className={`rounded-xl border p-4 ${selected ? "border-essred ring-2 ring-essred/20 bg-essred/5" : "bg-white"}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-display font-bold text-slate-900">{lbl}</div>
                      {selected && <span className="text-[10px] bg-essred text-white px-2 py-0.5 rounded font-semibold">SELECTED</span>}
                    </div>
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Company bears</span><span className="font-mono font-semibold text-slate-900">{fmt(tc.company_bears_usd, "USD")}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Employee bears</span><span className="font-mono font-semibold">{fmt(tc.employee_bears_usd, "USD")}</span></div>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground leading-snug">{tc.note}</p>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        {/* Scenario */}
        <TabsContent value="usmodel">
          <USSalaryModelTab />
        </TabsContent>

        <TabsContent value="scenario">
          <Card className="p-6 bg-white" data-testid="indiv-scenario-panel">
            <h3 className="font-display font-bold text-slate-900 mb-1">Scenario Analysis</h3>
            <p className="text-sm text-muted-foreground mb-5">Adjust FX, rent and COLA assumptions to see cost sensitivity. Base calculation is unchanged.</p>
            {[["fx", "FX rate change", scenario.fx], ["rent", "Host rent change", scenario.rent], ["cola", "COLA index change", scenario.cola]].map(([k, lbl, val]) => (
              <div key={k} className="mb-5">
                <div className="flex justify-between text-sm mb-2"><span className="font-medium">{lbl}</span><span className="font-mono text-essred">{val > 0 ? "+" : ""}{val}%</span></div>
                <Slider data-testid={`indiv-scenario-slider-${k}`} min={-30} max={30} step={1} value={[val]}
                  onValueChange={([v]) => runScenario({ ...scenario, [k]: v })} />
              </div>
            ))}
            {scResult && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <Metric label="Scenario Employer Cost" value={fmt(scResult.scenario_total_employer_cost, hc)} sub={`Base: ${fmt(scResult.base_total_employer_cost, hc)}`} accent={scResult.delta_pct >= 0 ? "text-essred" : "text-emerald-600"} />
                <Metric label="Cost Delta" value={`${scResult.delta_pct > 0 ? "+" : ""}${scResult.delta_pct}%`} accent={scResult.delta_pct >= 0 ? "text-essred" : "text-emerald-600"} />
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Sources */}
        <TabsContent value="sources">
          <Card className="p-6 bg-white" data-testid="indiv-source-confidence-panel">
            <h3 className="font-display font-bold text-slate-900 mb-4">Assumptions & Source Confidence</h3>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Assumption label="Data source" value={r.data_source_label} />
              <Assumption label="Reference period" value={r.reference_period} />
              <Assumption label="Retrieved" value={r.retrieved_at?.slice(0, 10)} />
              <Assumption label="Confidence" value={<ConfidenceBadge level={r.confidence} />} />
              <Assumption label="Tax policy" value={r.tax_policy} />
              <Assumption label="COLA policy applied" value={r.cola_policy_applied} />
              <Assumption label="Gross-up converged" value={r.gross_up_converged ? `Yes (${r.gross_up_iterations} iters, ±1)` : "No"} />
              <Assumption label="Social-security exemption" value={r.social_security_exempt ? `Applied — ${r.agreement?.certificate}` : "Not applied — no confirmed agreement"} />
            </div>
            <div className="mt-5 rounded-lg bg-slate-50 border p-4 text-xs text-muted-foreground flex gap-2">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              These figures derive from the ESSAR Public-Data Mobility Index (public composite sources) and deterministic tax rules. They are NOT Mercer figures — Mercer values appear only after a licensed dataset is imported.
            </div>
          </Card>
        </TabsContent>

        {/* Workflow */}
        <TabsContent value="workflow">
          <Card className="p-6 bg-white" data-testid="workflow-stepper">
            <h3 className="font-display font-bold text-slate-900 mb-5">Approval Workflow</h3>
            <div className="flex flex-wrap gap-2 items-center mb-6">
              {STAGES.map((st, i) => {
                const cur = STAGES.indexOf(doc.status);
                const done = i < cur, active = i === cur;
                return (
                  <React.Fragment key={st}>
                    <div className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${active ? "text-white" : done ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`} style={active ? { background: STAGE_COLORS[st] } : {}}>
                      {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />} {st}
                    </div>
                    {i < STAGES.length - 1 && <div className="h-px w-4 bg-slate-200 hidden sm:block" />}
                  </React.Fragment>
                );
              })}
            </div>
            {canApprove && doc.status !== "Employee Issued" && doc.status !== "Superseded" && (
              <div className="flex gap-3">
                <Button data-testid="workflow-approve-btn" onClick={() => advance("advance")} disabled={advancing} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {advancing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Advance stage"}
                </Button>
                <Button data-testid="workflow-reject-btn" onClick={() => advance("reject")} disabled={advancing} variant="outline" className="border-essred text-essred hover:bg-essred hover:text-white">
                  Send back to Draft
                </Button>
              </div>
            )}
            <div className="mt-6" data-testid="workflow-audit-log">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Sign-off log</div>
              {(doc.approvals || []).length === 0 ? <div className="text-sm text-muted-foreground">No approvals recorded yet.</div> :
                (doc.approvals || []).map((a, i) => (
                  <div key={i} className="text-sm border-l-2 border-[#2A3542] pl-3 py-1.5">
                    <span className="font-medium">{a.from} → {a.to}</span> · {a.by} ({a.role}) · <span className="text-muted-foreground">{a.at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                ))}
            </div>
          </Card>
        </TabsContent>

        {/* City Guide */}
        <TabsContent value="guide">
          {guide?.available ? <CityGuide guide={guide} /> : <Card className="p-6 bg-white text-muted-foreground">City guide unavailable for this location.</Card>}
        </TabsContent>
      </Tabs>

      {/* Exports */}
      <Card className="mt-6 p-5 bg-white flex flex-wrap items-center gap-3" data-testid="export-panel">
        <span className="font-display font-semibold text-slate-900 mr-2">Export approved proposal:</span>
        <Button data-testid="export-pdf-btn" onClick={() => doExport("pdf")} disabled={!!exporting} variant="outline" className="border-[#2A3542] text-slate-900 hover:bg-slate-900 hover:text-white">
          {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />} PDF
        </Button>
        <Button data-testid="export-docx-btn" onClick={() => doExport("docx")} disabled={!!exporting} variant="outline" className="border-[#2A3542] text-slate-900 hover:bg-slate-900 hover:text-white">
          {exporting === "docx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileType className="h-4 w-4 mr-2" />} DOCX
        </Button>
        <Button data-testid="export-xlsx-btn" onClick={() => doExport("xlsx")} disabled={!!exporting} variant="outline" className="border-[#2A3542] text-slate-900 hover:bg-slate-900 hover:text-white">
          {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />} XLSX
        </Button>
      </Card>

      <AIAssistant calculationId={id} />
    </div>
  );
}

function Assumption({ label, value }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-medium text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function CityGuide({ guide }) {
  const g = guide.guide;
  const SECTIONS = [
    ["Overview", g.overview], ["Housing", g.housing], ["Healthcare", `${g.healthcare} · Emergency: ${g.emergency}`],
    ["Immigration", g.immigration], ["Tax & Social Security", g.tax], ["Banking & Currency", g.banking],
    ["Transport", g.transport], ["Telecom", g.telecom], ["Climate", g.climate], ["Security", g.security],
    ["Etiquette", g.etiquette],
  ];
  return (
    <div data-testid="relocation-guide-container" className="space-y-4">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
        <strong>Informational only</strong> · retrieved {guide.retrieved_at}. {guide.disclaimer}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {SECTIONS.map(([t, c]) => (
          <Card key={t} className="p-4 bg-white" data-testid={`guide-section-${t.toLowerCase().split(" ")[0]}`}>
            <div className="text-xs uppercase tracking-widest text-essred font-semibold mb-1">{t}</div>
            <p className="text-sm text-slate-300 leading-relaxed">{c}</p>
          </Card>
        ))}
        <Card className="p-4 bg-white" data-testid="relocation-tab-neighborhoods">
          <div className="text-xs uppercase tracking-widest text-essred font-semibold mb-2">Neighbourhoods</div>
          <ul className="text-sm space-y-1">{g.neighbourhoods.map((n) => <li key={n} className="text-slate-300">• {n}</li>)}</ul>
        </Card>
        <Card className="p-4 bg-white" data-testid="relocation-tab-schools">
          <div className="text-xs uppercase tracking-widest text-essred font-semibold mb-2">Schools</div>
          <ul className="text-sm space-y-1">{g.schools.map((n) => <li key={n} className="text-slate-300">• {n}</li>)}</ul>
        </Card>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(guide.checklists).map(([k, items]) => (
          <Card key={k} className="p-4 bg-white" data-testid="relocation-checklist">
            <div className="text-xs uppercase tracking-widest text-slate-900 font-semibold mb-2">{k.replace(/_/g, " ")}</div>
            <ul className="text-sm space-y-1.5">{items.map((it) => <li key={it} className="flex gap-2 text-slate-300"><CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{it}</li>)}</ul>
          </Card>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-4 bg-emerald-50 border-emerald-200">
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-2">Do</div>
          <ul className="text-sm space-y-1">{guide.do_dont.do.map((d) => <li key={d} className="text-emerald-900">✓ {d}</li>)}</ul>
        </Card>
        <Card className="p-4 bg-essred/5 border-essred/20">
          <div className="text-xs uppercase tracking-widest text-essred font-semibold mb-2">Don't</div>
          <ul className="text-sm space-y-1">{guide.do_dont.dont.map((d) => <li key={d} className="text-essred-700">✗ {d}</li>)}</ul>
        </Card>
      </div>
      <Card className="p-4 bg-white">
        <div className="text-xs uppercase tracking-widest text-slate-900 font-semibold mb-2">Official links</div>
        <div className="flex flex-wrap gap-2">{g.official_links.map((l) => <a key={l} href={l} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">{l}</a>)}</div>
      </Card>
    </div>
  );
}
